import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

export const DropdownContext = createContext({
    openDropdownId: null,
    setOpenDropdownId: () => console.warn('DropdownContext: setOpenDropdownId called outside DropdownProvider'),
    closeDropdown: () => console.warn('DropdownContext: closeDropdown called outside DropdownProvider'),
});

export const useDropdown = () => {
    return useContext(DropdownContext);
};

export const DropdownProvider = ({ children }) => {
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const ignoreNextClickRef = useRef(false);

    const closeDropdown = () => {
        setOpenDropdownId(null);
    };

    const openDropdown = (id) => {
        ignoreNextClickRef.current = true;
        setOpenDropdownId(id);
    };

    useEffect(() => {
        if (openDropdownId === null) return;

        const handleClick = (event) => {
            if (!event.isTrusted) return;

            if (ignoreNextClickRef.current) {
                ignoreNextClickRef.current = false;
                return;
            }

            // Walk up from the clicked element rather than using getElementById —
            // a defensive guard in case duplicate `${id}-container` IDs ever appear
            // on the page (e.g. multiple providers under the same parent), so we
            // resolve the click's actual ancestor container rather than the first
            // DOM match.
            const isInsideDropdown = !!event.target.closest?.(`#${openDropdownId}-container`);
            const isInSelect2Overlay = !!event.target.closest?.('.select2-dropdown');
            // A confirmation dialog renders in a portal outside the container; clicks in it keep the dropdown open.
            const isInConfirmationDialog = !!event.target.closest?.('.confirmation-dialog-modal');

            if (!isInsideDropdown && !isInSelect2Overlay && !isInConfirmationDialog) {
                closeDropdown();
            }
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener('click', handleClick);
        }, 50);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('click', handleClick);
        };
    }, [openDropdownId]);

    return (
        <DropdownContext.Provider value={{ openDropdownId, setOpenDropdownId: openDropdown, closeDropdown }}>
            {children}
        </DropdownContext.Provider>
    );
};
